import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    Divider,
    alpha, useTheme
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {ApiDetails, KEY} from "../../../dummyData";
import CryptoJS from "crypto-js";
import LeftSide from "./Left Side/LeftSide";
import RightSide from "./Right Side/RightSide";
import {ErrorMessage, Field, Form, Formik} from "formik";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
// import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import {showSnackbar} from "../../../components/SnackBar/SnackBar";
import useGoBack from "../../../components/useGoBack";
import WaterDropIcon from "@mui/icons-material/Water";

// Enhanced Deposit Management Component
const DepositManagement = ({open, onClose, onSave}) => {
    const theme = useTheme();
    const [depositItems, setDepositItems] = useState([{description: '', amount: '', refundable: ''}]);
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [refundableDeposit, setRefundableDeposit] = useState(0);
    const [depositSet, setDepositSet] = useState(false);

    const addDepositItem = () => {
        setDepositItems([...depositItems, {description: '', amount: '', refundable: ''}]);
    };

    const updateDepositItem = (index, field, value) => {
        const updatedItems = [...depositItems];
        updatedItems[index][field] = value;
        setDepositItems(updatedItems);
        setDepositSet(false);
    };

    const removeDepositItem = (index) => {
        if (depositItems.length > 1) {
            const updatedItems = depositItems.filter((_, i) => i !== index);
            setDepositItems(updatedItems);
            setDepositSet(false);
        }
    };

    const calculateDeposit = () => {
        try {
            let total = 0;
            let refundable = 0;

            depositItems.forEach(item => {
                if (!item.description || !item.amount || !item.refundable) {
                    throw new Error("Please fill in all deposit fields");
                }

                if (item.refundable.toUpperCase() !== "YES" && item.refundable.toUpperCase() !== "NO") {
                    throw new Error("Refundable field must be YES or NO");
                }

                const amount = parseFloat(item.amount);
                total += amount;

                if (item.refundable.toUpperCase() === "YES") {
                    refundable += amount;
                }
            });

            setTotalDeposit(total);
            setRefundableDeposit(refundable);
            setDepositSet(true);
        } catch (error) {
            showSnackbar({
                title: "Error!",
                message: error.message,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const handleSave = () => {
        if (!depositSet) {
            showSnackbar({
                title: "Error!",
                message: "Please calculate deposit first",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        onSave({
            items: depositItems,
            totalDeposit,
            refundableDeposit
        });
        onClose();
    };

    const resetForm = () => {
        setDepositItems([{description: '', amount: '', refundable: ''}]);
        setTotalDeposit(0);
        setRefundableDeposit(0);
        setDepositSet(false);
    };

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{
                backgroundColor: "#2C5283",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            }}>
                <Typography variant="h6">Add Deposit Details</Typography>
                <IconButton onClick={onClose} sx={{color: "white"}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: 3}}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
                    <Typography variant="h6" fontWeight="bold">Deposit Items</Typography>
                    <Button
                        onClick={addDepositItem}
                        variant="contained"
                        size="small"
                        sx={{
                            background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                            textTransform: "none"
                        }}
                    >
                        Add Item
                    </Button>
                </Box>

                {depositItems.map((item, index) => (
                    <Box key={index} sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                    }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Item {index + 1}
                            </Typography>
                            {depositItems.length > 1 && (
                                <Button
                                    onClick={() => removeDepositItem(index)}
                                    size="small"
                                    color="error"
                                    sx={{textTransform: "none"}}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={item.description}
                                    onChange={(e) => updateDepositItem(index, 'description', e.target.value)}
                                    size="small"
                                    placeholder="e.g., House Deposit"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Amount (KSH)"
                                    type="number"
                                    value={item.amount}
                                    onChange={(e) => updateDepositItem(index, 'amount', e.target.value)}
                                    size="small"
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={item.refundable}
                                        onChange={(e) => updateDepositItem(index, 'refundable', e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Refundable</MenuItem>
                                        <MenuItem value="YES">YES - Refundable</MenuItem>
                                        <MenuItem value="NO">NO - Non-refundable</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                <Box sx={{mt: 3, mb: 2}}>
                    <Button
                        onClick={calculateDeposit}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: "#2C5283",
                            color: "#2C5283",
                            '&:hover': {
                                borderColor: "#1E3A63",
                                backgroundColor: alpha("#2C5283", 0.04)
                            }
                        }}
                    >
                        Calculate Deposit
                    </Button>
                </Box>

                {depositSet && (
                    <Box sx={{
                        p: 3,
                        backgroundColor: alpha("#4CAF50", 0.1),
                        borderRadius: 2,
                        border: "1px solid #4CAF50"
                    }}>
                        <Typography variant="h6" fontWeight="bold" sx={{mb: 2, color: "#2E7D32"}}>
                            Deposit Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Total Deposit</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {totalDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Refundable Amount</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {refundableDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                Non-refundable: KSH {(totalDeposit - refundableDeposit).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{p: 3}}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!depositSet}
                    sx={{
                        background: `linear-gradient(45deg, #4CAF50, #2E7D32)`,
                        '&:disabled': {
                            backgroundColor: '#ccc'
                        }
                    }}
                >
                    Save Deposit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const PropertyDetails = (props) => {
    const matches = useMediaQuery("(max-width:700px)");

    let myMapDetailsDecrypted = "";

    try {
        myMapDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.getItem("myMap"), KEY);
    } catch (e) {
        console.log(e);
    }

    const originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    const [tenants, setTenants] = useState([])
    const [property, setProperty] = useState({
        agency_id: "",
        apply_penalty: "",
        consumer_secret: "",
        electricity_bill: "",
        expected_income: "",
        ho_number: "",
        income_received: "",
        landlord_id: "",
        location: "",
        minimum_days_to_vacate: "",
        minimum_electricity_bill: "",
        minimum_water_bill: "",
        occupancy: "",
        other_charges: "",
        pass_key: "",
        payment_details: "",
        penalty_date: "",
        penalty_percentage: "",
        property_id: "",
        property_name: "",
        query_number: "",
        reminder_date: "",
        short_code: "",
        till_number: "",
        total_property_units: "",
        transaction_type: "",
        water_bill: "",
    });

    const [propertyImage, setPropertyImage] = useState(null);
    const [billTenantsModal, setBillTenantsModal] = useState(false);
    const [addTenantModal, setAddTenantModal] = useState(false);
    const [depositModal, setDepositModal] = useState(false);

    // Enhanced state management
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedUnitWaterReading, setSelectedUnitWaterReading] = useState(null);
    const [selectedUnitElectricityReading, setSelectedUnitElectricityReading] = useState(null);
    const [depositDetails, setDepositDetails] = useState(null);
    const [depositAsArrears, setDepositAsArrears] = useState(true);
    const [rentAsArrears, setRentAsArrears] = useState(true);
    const [tenantLoading, setTenantLoading] = useState(false);

    // File uploads (keeping as null for now)
    const [tenantPhoto] = useState(null);
    const [photoOfNationalID] = useState(null);
    const [copyOfContract] = useState(null);
    const [inventoryChecklist] = useState(null);

    const initialValues = {
        first_name: "",
        last_name: "",
        phone_number: "",
        secondary_phone_number: "",
        national_id: "",
        rent: "",
        next_of_kin_first_name: "",
        next_of_kin_last_name: "",
        next_of_kin_relationship: "",
        next_of_kin_contacts: "",
        next_of_kin_national_id: "",
        occupation_or_profession: "",
        institution_name: "",
        place_of_work: "",
        reason_for_relocating: "",
        previous_residence: "",
        email_address: "",
        landlord_id: JSON.parse(originalText)[0][1],
    };

    useEffect(() => {
        fetch(ApiDetails + "pegasus/visionary/property/get/Specific/property", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                property_id: props.property_id,
                landlord_id: JSON.parse(originalText)[0][1],
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setProperty(data);
                    // Fetch units when property is loaded
                    getUnits(data.property_id);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed fetching property details.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed fetching property details.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });

        getPropertyPhoto()
    }, []);

    // Fetch tenants
    useEffect(() => {
        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({property_id: props.property_id}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setTenants(data);
                }
            })
            .catch((err) => console.log(err));
    }, [props.property_id]);

    const getPropertyPhoto = () => {
        fetch(ApiDetails + "pegasus/visionary/property/get/Specific/property/photo", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                property_id: props.property_id,
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    let pic = data.property_image;
                    if (pic.base64 !== "No File Found") {
                        setPropertyImage("data:image/jpeg;base64," + pic.base64);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    const getUnits = (propertyID) => {
        if (!propertyID) {
            console.log("Property ID is missing");
            return;
        }

        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({property_id: propertyID}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setUnits(data);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong loading units.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong loading units.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const handleClose = () => {
        setDepositModal(false);
        setAddTenantModal(false);
        // Reset form state
        setSelectedUnit(null);
        setSelectedUnitWaterReading(null);
        setSelectedUnitElectricityReading(null);
        setDepositDetails(null);
        setDepositAsArrears(true);
        setRentAsArrears(true);
    };

    const handleDepositSave = (deposit) => {
        setDepositDetails(deposit);
        setDepositModal(false);
        setAddTenantModal(true);
    };

    const addTenant = (values) => {
        setTenantLoading(true);

        // Validation
        if (values.first_name === "" || values.last_name === "") {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "First and last names are required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (values.phone_number === "" || values.phone_number.length < 10) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Enter a valid phone number",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (selectedUnit === null) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please select a unit",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (values.rent === "") {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Rent amount is required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!depositDetails) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please add deposit details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        console.log("Values: ");
        console.log(values);

        // Prepare data
        values.rent_as_arrear = rentAsArrears;
        values.deposit_as_arrears = depositAsArrears;
        values.is_student = "NO";
        values.landlord_email_address = JSON.parse(originalText)[5][1];
        values.landlord_id = JSON.parse(originalText)[0][1];
        values.property_name = property.property_name;
        values.property_id = property.property_id;
        values.meter_reading = selectedUnitWaterReading;
        values.electricity_meter_reading = selectedUnitElectricityReading;
        values.house_number = selectedUnit.unit;
        values.deposit = depositDetails.items.map(item => [item.description, item.amount, item.refundable]);
        values.deposit_held = depositDetails.refundableDeposit;

        let formData = new FormData();
        formData.append("body", JSON.stringify(values));
        formData.append("photo_of_national_id", photoOfNationalID);
        formData.append("copy_of_contract", copyOfContract);
        formData.append("inventory_checklist", inventoryChecklist);
        formData.append("tenant_photo", tenantPhoto);

        fetch(ApiDetails + "pegasus/visionary/tenant/add/tenant", {
            method: "POST",
            body: formData,
        })
            .then(async (response) => {
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Tenant added successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    window.location.reload();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Please try again later",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setTenantLoading(false);
            })
            .catch((err) => {
                setTenantLoading(false);
                console.log(err)
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const goBack = useGoBack();

    return (
        <>
            {/* Enhanced Deposit Management Dialog */}
            <DepositManagement
                open={depositModal}
                onClose={() => {
                    setDepositModal(false);
                    setAddTenantModal(true);
                }}
                onSave={handleDepositSave}
            />

            {/* Enhanced Add Tenant Dialog */}
            {/* Enhanced Add Tenant Dialog */}
            <Dialog
                maxWidth="lg"
                open={addTenantModal}
                keepMounted
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha("#2C5283", 0.1)}, ${alpha("#2C5283", 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha("#2C5283", 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: "#2C5283"
                                }}
                            >
                                <PersonAddIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Add New Tenant
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {property.property_name} • {property.location}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{p: 3, zoom: matches ? "85%" : "100%"}}>
                    <Formik initialValues={initialValues} onSubmit={addTenant}>
                        {(formikProps) => (
                            <Form noValidate>
                                <Grid container spacing={3}>
                                    {/* Personal Details Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2}}>Personal Details</Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    {["first_name", "last_name", "phone_number", "secondary_phone_number", "national_id", "rent"].map((field, index) => (
                                        <Grid item xs={12} sm={4} key={index}>
                                            <Field
                                                as={TextField}
                                                type={field === "rent" ? "number" : "text"}
                                                name={field}
                                                label={field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                                fullWidth
                                                variant="outlined"
                                                error={formikProps.errors[field] && formikProps.touched[field]}
                                                helperText={<ErrorMessage name={field}/>}
                                                required={["first_name", "last_name", "phone_number", "rent"].includes(field)}
                                            />
                                        </Grid>
                                    ))}

                                    {/* Property Selection */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2, mt: 2}}>Property Details</Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" required>
                                            <Select
                                                value={selectedUnit || ""}
                                                onChange={(e) => {
                                                    const unitData = e.target.value;
                                                    setSelectedUnit(unitData);
                                                    setSelectedUnitWaterReading(unitData.meter_reading);
                                                    setSelectedUnitElectricityReading(unitData.electricity_meter_reading);
                                                }}
                                                displayEmpty
                                                disabled={!units || units.length === 0}
                                            >
                                                <MenuItem value="" disabled>Select Unit</MenuItem>
                                                {units && units.length > 0 ? (
                                                    units.map((unit, index) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={unit}
                                                            disabled={unit.state === "OCCUPIED"}
                                                        >
                                                            {unit.unit}
                                                            <Box component="span" sx={{
                                                                ml: 1,
                                                                color: unit.state === "OCCUPIED" ? "error.main" : "success.main",
                                                                fontSize: "0.8rem"
                                                            }}>
                                                                ({unit.state.toLowerCase()})
                                                            </Box>
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled>No units available</MenuItem>
                                                )}
                                            </Select>
                                            <FormHelperText>
                                                {!units || units.length === 0 ? "No available units" : "Required"}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => {
                                                setAddTenantModal(false);
                                                setDepositModal(true);
                                            }}
                                            sx={{
                                                height: 56,
                                                textTransform: "none",
                                                backgroundColor: depositDetails ? alpha("#4CAF50", 0.1) : "#FBF4F0",
                                                borderColor: depositDetails ? "#4CAF50" : "#e0e0e0",
                                                color: depositDetails ? "#4CAF50" : "#2C5283"
                                            }}
                                        >
                                            {depositDetails ?
                                                `Deposit Set: KSH ${depositDetails.totalDeposit.toLocaleString()}` :
                                                "Add Deposit Details"
                                            }
                                        </Button>
                                    </Grid>

                                    {/* Water and Electricity Meter Readings */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Water Meter Reading"
                                            type="number"
                                            value={selectedUnitWaterReading || ''}
                                            onChange={(e) => setSelectedUnitWaterReading(e.target.value)}
                                            disabled={selectedUnitWaterReading === null}
                                            InputProps={{
                                                startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                                            }}
                                            placeholder={selectedUnitWaterReading === null ? "Select unit first" : "Enter reading"}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Electricity Meter Reading"
                                            type="number"
                                            value={selectedUnitElectricityReading || ''}
                                            onChange={(e) => setSelectedUnitElectricityReading(e.target.value)}
                                            disabled={selectedUnitElectricityReading === null}
                                            InputProps={{
                                                startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                                            }}
                                            placeholder={selectedUnitElectricityReading === null ? "Select unit first" : "Enter reading"}
                                        />
                                    </Grid>

                                    {/* Billing Options */}
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Typography>Add Deposit As Arrears</Typography>
                                            <Switch
                                                checked={depositAsArrears}
                                                onChange={(e) => setDepositAsArrears(e.target.checked)}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Typography>Add Rent As Arrears</Typography>
                                            <Switch
                                                checked={rentAsArrears}
                                                onChange={(e) => setRentAsArrears(e.target.checked)}
                                            />
                                        </Box>
                                    </Grid>

                                    {/* Next of Kin Details Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2, mt: 2}}>Next of Kin Details</Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    {["next_of_kin_first_name", "next_of_kin_last_name", "next_of_kin_relationship", "next_of_kin_contacts", "next_of_kin_national_id"].map((field, index) => (
                                        <Grid item xs={12} sm={4} key={index}>
                                            <Field
                                                as={TextField}
                                                type="text"
                                                name={field}
                                                label={field.replace(/_/g, " ").replace("next of kin", "").replace(/\b\w/g, l => l.toUpperCase()).trim()}
                                                fullWidth
                                                variant="outlined"
                                                error={formikProps.errors[field] && formikProps.touched[field]}
                                                helperText={<ErrorMessage name={field}/>}
                                            />
                                        </Grid>
                                    ))}

                                    {/* Additional Details */}
                                    {/*<Grid item xs={12}>*/}
                                    {/*    <Typography variant="h6" sx={{mb: 2, mt: 2}}>Additional Information*/}
                                    {/*        (Optional)</Typography>*/}
                                    {/*    <Divider sx={{mb: 2}}/>*/}
                                    {/*</Grid>*/}

                                    {/*{["occupation_or_profession", "institution_name", "place_of_work", "reason_for_relocating", "previous_residence", "email_address"].map((field, index) => (*/}
                                    {/*    <Grid item xs={12} sm={6} key={index}>*/}
                                    {/*        <Field*/}
                                    {/*            as={TextField}*/}
                                    {/*            type={field === "email_address" ? "email" : "text"}*/}
                                    {/*            name={field}*/}
                                    {/*            label={field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}*/}
                                    {/*            fullWidth*/}
                                    {/*            variant="outlined"*/}
                                    {/*            error={formikProps.errors[field] && formikProps.touched[field]}*/}
                                    {/*            helperText={<ErrorMessage name={field}/>}*/}
                                    {/*        />*/}
                                    {/*    </Grid>*/}
                                    {/*))}*/}

                                    {/* Buttons */}
                                    <Grid item xs={12} container justifyContent="flex-end" sx={{mt: 2}}>
                                        <Button onClick={handleClose} color="inherit" sx={{mr: 2}}>
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            loading={tenantLoading}
                                            variant="contained"
                                            startIcon={<PersonAddIcon/>}
                                            sx={{
                                                background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                                                px: 3
                                            }}
                                        >
                                            Add Tenant
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            <Box p={1} bgcolor="#F7F9FA">
                {/* Header Section */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 50,
                        zIndex: 1000,
                        backgroundColor: "#F5F5F5",
                        borderBottom: "2px solid #2C5283",
                        padding: "10px 10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {/* Left Section: Back button and Title */}
                    <Box sx={{display: "flex", alignItems: "center"}}>
                        <IconButton
                            sx={{color: "#2C5283", marginRight: 1, backgroundColor: "#F4F6F8"}}
                            onClick={goBack}
                        >
                            <ArrowBackIcon/>
                        </IconButton>
                        <Typography variant="h6" sx={{fontWeight: "bold", color: "#2C5283"}}>
                            View Property - {property.property_name.toUpperCase()}
                        </Typography>
                    </Box>

                    {/* Right Section: Action Buttons */}
                    <Box sx={{display: "flex", gap: 1}}>
                        <Button
                            variant="outlined"
                            sx={{textTransform: "none", backgroundColor: "#FBF4F0"}}
                            onClick={() => setAddTenantModal(true)}
                        >
                            Add Tenant
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{textTransform: "none", backgroundColor: "#FBF4F0"}}
                            onClick={() => window.location.reload()}
                        >
                            Refresh Details
                        </Button>
                    </Box>
                </Box>

                {/* Main Layout */}
                <Grid container spacing={3}>
                    <LeftSide propertyDetails={property} propertyImage={propertyImage}/>
                    <RightSide
                        propertyDetails={property}
                        tenants={tenants}
                        setBillTenantsModal={setBillTenantsModal}
                        billTenantsModal={billTenantsModal}
                    />
                </Grid>
            </Box>
        </>
    )
}

export default PropertyDetails;